/**
 * The whole walkthrough's state.
 *
 * Everything downstream of the picked file is derived, so changing the chunk
 * size at any point rebuilds the tree from scratch. That is deliberate: the
 * point of the chunk size slide is to watch the root hash move, which only
 * works if the rebuild is pure and cheap.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generateSecretKey, getPublicKey } from "nostr-tools/pure";
import { npubEncode, nsecEncode } from "nostr-tools/nip19";
import { toHex } from "./bytes";
import { DEFAULT_SERVER } from "./blossom";
import {
  CANONICAL_CHUNK_SIZE,
  CANONICAL_MAX_LINKS,
  buildDirectoryNode,
  buildFileNode,
  collectBlobs,
  planDirectory,
  planFile,
  resetNodeIds,
  validateEntryName,
  type BuiltNode,
  type DirectoryShape,
  type FileGeometry,
  type SplitOptions,
} from "./nodes";
import { DEFAULT_RELAYS, type Visibility } from "./nostrRoot";

/** Below this size we re-derive hashes live while the slider moves. */
const LIVE_HASH_LIMIT = 1024 * 1024;
const REBUILD_DEBOUNCE_MS = 120;

export interface PickedFile {
  name: string;
  bytes: Uint8Array;
  type: string;
}

export interface BuiltTree {
  /** The file itself: a raw blob, or a `t = 1` manifest when chunked. */
  file: BuiltNode;
  /** The user-named folder holding the file. */
  folder: BuiltNode;
  /** The `t = 2` root the nostr event will point at. */
  root: BuiltNode;
  /** Every blob needing upload, children before parents. */
  blobs: BuiltNode[];
}

export interface Identity {
  secretKey: Uint8Array;
  publicKey: string;
  npub: string;
  nsec: string;
}

export type UploadState = "pending" | "uploading" | "done" | "error";

export interface UploadRow {
  node: BuiltNode;
  state: UploadState;
  message?: string;
  hashMatches?: boolean;
}

export interface HashtreeBuild {
  // --- inputs -------------------------------------------------------------
  file: PickedFile | null;
  setFile: (file: PickedFile | null) => void;
  folderName: string;
  setFolderName: (name: string) => void;
  /** Why the current folder name cannot be used, or `null` when it is fine. */
  folderNameError: string | null;
  treeName: string;
  setTreeName: (name: string) => void;
  chunkSize: number;
  setChunkSize: (size: number) => void;
  maxLinks: number;
  setMaxLinks: (count: number) => void;
  server: string;
  setServer: (server: string) => void;
  relays: string[];
  setRelays: (relays: string[]) => void;
  visibility: Visibility;
  setVisibility: (visibility: Visibility) => void;

  // --- derived ------------------------------------------------------------
  split: SplitOptions;
  /** Shape of the file at the current settings. Recomputed synchronously. */
  geometry: FileGeometry | null;
  /** Shape the folder would take. Only interesting in the fanout sandbox. */
  folderShape: DirectoryShape;
  /** The built, encrypted tree. Lags the slider by a debounce tick. */
  tree: BuiltTree | null;
  building: boolean;
  /** True while the tree does not yet reflect the current settings. */
  stale: boolean;
  buildError: string | null;

  // --- publishing ---------------------------------------------------------
  identity: Identity;
  regenerateIdentity: () => void;
  uploads: UploadRow[];
  setUploads: (rows: UploadRow[]) => void;
  resetUploads: () => void;
}

function makeIdentity(): Identity {
  const secretKey = generateSecretKey();
  const publicKey = getPublicKey(secretKey);
  return { secretKey, publicKey, npub: npubEncode(publicKey), nsec: nsecEncode(secretKey) };
}

export function useHashtreeBuild(): HashtreeBuild {
  const [file, setFileState] = useState<PickedFile | null>(null);
  const [folderName, setFolderName] = useState("my-folder");
  const [treeName, setTreeName] = useState("my-first-hashtree");
  // Start well below canonical so a normal file actually chunks, which is the
  // only way the BUD-17 slides have anything real to show.
  const [chunkSize, setChunkSize] = useState(1024);
  const [maxLinks, setMaxLinks] = useState(CANONICAL_MAX_LINKS);
  const [server, setServer] = useState(DEFAULT_SERVER);
  const [relays, setRelays] = useState<string[]>(DEFAULT_RELAYS);
  const [visibility, setVisibility] = useState<Visibility>("public");

  const [identity, setIdentity] = useState<Identity>(makeIdentity);
  const [tree, setTree] = useState<BuiltTree | null>(null);
  const [building, setBuilding] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [uploads, setUploads] = useState<UploadRow[]>([]);

  const split = useMemo<SplitOptions>(() => ({ chunkSize, maxLinks }), [chunkSize, maxLinks]);

  // Geometry is pure arithmetic over the file length, so it can run on every
  // render while the slider is moving.
  const geometry = useMemo(
    () => (file ? planFile(file.bytes.length, split) : null),
    [file, split],
  );

  const folderShape = useMemo(() => planDirectory(1, { maxLinks }), [maxLinks]);

  const setFile = useCallback((next: PickedFile | null) => {
    setFileState(next);
    setTree(null);
    setUploads([]);
    setBuildError(null);
  }, []);

  const resetUploads = useCallback(() => setUploads([]), []);

  const regenerateIdentity = useCallback(() => {
    setIdentity(makeIdentity());
    setUploads([]);
  }, []);

  // An invalid folder name is a normal thing to pass through while typing — the
  // field is empty for a moment every time someone clears it. Validate here so
  // the build can decline to run, rather than throwing and destroying the tree.
  const folderNameError = useMemo(() => validateEntryName(folderName), [folderName]);

  // A build is only current if it matches the inputs that produced it.
  const signature = useMemo(
    () => (file ? `${file.name}:${file.bytes.length}:${chunkSize}:${maxLinks}:${folderName}` : null),
    [file, chunkSize, maxLinks, folderName],
  );
  const builtSignature = useRef<string | null>(null);
  const stale = signature !== null && builtSignature.current !== signature && !folderNameError;

  useEffect(() => {
    if (!file || !signature) return;
    if (builtSignature.current === signature) return;
    // Keep showing the last good tree until the name is usable again.
    if (folderNameError) return;

    // Large files would make the slider janky, so wait for the drag to settle.
    const immediate = file.bytes.length <= LIVE_HASH_LIMIT;
    let cancelled = false;

    const run = async () => {
      setBuilding(true);
      setBuildError(null);
      try {
        // Reset ids so the same inputs always yield the same node ids, which
        // keeps React keys and inspector selection stable across rebuilds.
        resetNodeIds();

        const built = await buildFileNode(file.bytes, file.name, split);
        const folder = await buildDirectoryNode([{ name: file.name, node: built }], folderName, split);
        const root = await buildDirectoryNode([{ name: folderName, node: folder }], "root", split);

        if (cancelled) return;
        builtSignature.current = signature;
        setTree({ file: built, folder, root, blobs: collectBlobs(root) });
        setUploads([]);
      } catch (error) {
        if (cancelled) return;
        setBuildError(error instanceof Error ? error.message : String(error));
        setTree(null);
      } finally {
        if (!cancelled) setBuilding(false);
      }
    };

    if (immediate) {
      void run();
      return () => {
        cancelled = true;
      };
    }

    const timer = setTimeout(() => void run(), REBUILD_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [file, signature, split, folderName, folderNameError]);

  return {
    file,
    setFile,
    folderName,
    setFolderName,
    folderNameError,
    treeName,
    setTreeName,
    chunkSize,
    setChunkSize,
    maxLinks,
    setMaxLinks,
    server,
    setServer,
    relays,
    setRelays,
    visibility,
    setVisibility,

    split,
    geometry,
    folderShape,
    tree,
    building,
    stale,
    buildError,

    identity,
    regenerateIdentity,
    uploads,
    setUploads,
    resetUploads,
  };
}

/** Preset chunk sizes offered beside the slider. */
export const CHUNK_PRESETS = [
  { label: "256 B", value: 256 },
  { label: "1 KiB", value: 1024 },
  { label: "16 KiB", value: 16 * 1024 },
  { label: "256 KiB", value: 256 * 1024 },
  { label: "2 MiB", value: CANONICAL_CHUNK_SIZE },
] as const;

/** The slider works in powers of two, which is easier to reason about than raw bytes. */
export const MIN_CHUNK_EXPONENT = 6; // 64 B
export const MAX_CHUNK_EXPONENT = 21; // 2 MiB

export const identityHex = (identity: Identity) => toHex(identity.secretKey);
